/**
 * Global stylesheet order. It matters: tokens declare the custom properties that
 * every later rule reads, and base.css consumes tokens from the five files above
 * it. site.css comes last so page rules can override component defaults.
 *
 * gatsby-ssr.tsx imports the same list in the same order — keep them in sync.
 */
import './src/styles/fonts.css'
import './src/styles/tokens/colors.css'
import './src/styles/tokens/typography.css'
import './src/styles/tokens/spacing.css'
import './src/styles/tokens/borders.css'
import './src/styles/tokens/motion.css'
import './src/styles/tokens/base.css'
import './src/styles/site.css'

import type { GatsbyBrowser } from 'gatsby'

/**
 * Land at the top of every page you navigate to.
 *
 * Gatsby scrolls for you, but `html { scroll-behavior: smooth }` turns that
 * into an animation that the next page's layout can interrupt — so you arrive
 * at a new page already part-way down it, looking at the middle of something
 * you have not read. Jumping explicitly, without the smooth behaviour, is the
 * only version that always works.
 *
 * Returning `false` tells Gatsby not to scroll again afterwards.
 *
 * Back and forward still restore where you were: that is a different intent,
 * and losing your place on the way back is its own annoyance.
 */
export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
  routerProps,
  getSavedScrollPosition,
}) => {
  // If we have been on this exact entry before, go back to where the reader
  // was. Gatsby does not reliably report the navigation type, so the presence
  // of a saved position is the signal — it only exists for an entry already
  // visited, which is precisely the back/forward case.
  const saved = routerProps?.location ? getSavedScrollPosition(routerProps.location) : null
  if (saved) return saved

  window.requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
  })
  return false
}
